import { EventModel } from '@bryntum/schedulerpro/schedulerpro.module.js';
import type { Calendar } from 'sr-types/lib/search/v1/graphql';
import { SupportedSchedulerDurationUnit } from '../types';
import {
    makeScheduleCalculationResultFromBryntumEvent,
    makesBryntumEventFromParsedScheduleCalculationInput,
    validateAndParseScheduleCalculationInput,
    validateAndParseScheduleCalculationInputList
} from './common-schedule-utils';
import { acquireSchedulerProjectInstance } from './scheduler-instance-manager';

const CALC_EVENT_ID = 'calc-event';
const CALC_RESOURCE_ID = 'calc-resource';
const CALC_ASSIGNMENT_ID = 'calc-assignment';
const CALC_RESOURCE_NAME = 'Calculation Resource';
const CALC_MULTI_EVENT_ID_PREFIX = 'calc-multi-event-';
const CALC_MULTI_ASSIGNMENT_ID_SUFFIX = '-assignment';
const CALC_MULTI_DEP_ID_PREFIX = 'calc-dep-';

type ScheduleCalculationInputBase = {
    /** Start date in YYYYMMDD format */
    startDate: string;
    /** Start time in HHmmss format (optional) */
    startTime?: string;
    /** Duration unit (hour, day, week, month, year) */
    durationUnit: SupportedSchedulerDurationUnit;
    /** Calendar for working time calculations (optional) */
    calendar?: Calendar;
    /** Locale for duration formatting (defaults to 'en-US'). Used when roundDuration is true. */
    locale?: string;
};

type ScheduleCalculationInputDurationSpecified = ScheduleCalculationInputBase & {
    /** If true, duration is specified and endDate is calculated */
    durationSpecified: true;
    /** Duration value to use for calculating end date */
    durationValue: number;
};

type ScheduleCalculationInputEndDateSpecified = ScheduleCalculationInputBase & {
    /** If false, endDate is specified and duration is calculated */
    durationSpecified: false;
    /** End date in YYYYMMDD format */
    endDate: string;
    /** End time in HHmmss format (optional) */
    endTime?: string;
};

export type ScheduleCalculationInput =
    | ScheduleCalculationInputDurationSpecified
    | ScheduleCalculationInputEndDateSpecified;

export interface ScheduleCalculationResult {
    /** Calculated or input start date in YYYYMMDD format */
    startDate: string;
    /** Calculated or input start time in HHmmss format */
    startTime?: string;
    /** Calculated or input end date in YYYYMMDD format */
    endDate: string;
    /** Calculated or input end time in HHmmss format */
    endTime?: string;
    /** Calculated or input duration value */
    durationValue: number;
    /** Duration unit */
    durationUnit: SupportedSchedulerDurationUnit;
}

/**
 * Calculates either the end date or duration based on schedule inputs using Bryntum's scheduler engine.
 * This function leverages Bryntum's calendar-aware scheduling capabilities to handle working days,
 * holidays, and time zones correctly.
 *
 * @param input - Schedule calculation parameters
 * @returns Promise resolving to the calculated schedule with all date/time/duration fields populated
 * @throws Error if calculation is already in progress or if required inputs are missing
 *
 * @example
 * // Calculate end date from start date and duration
 * const result = await calculateSchedule({
 *   startDate: '20260106',
 *   startTime: '09:00',
 *   durationValue: 5,
 *   durationUnit: 'day',
 *   durationSpecified: true
 * });
 *
 * @example
 * // Calculate duration from start and end dates
 * const result = await calculateSchedule(
 *   {
 *     startDate: '20260106',
 *     endDate: '20260110',
 *     durationUnit: 'day',
 *     durationSpecified: false
 *   }
 * );
 */
export async function calculateSchedule(input: ScheduleCalculationInput): Promise<ScheduleCalculationResult> {
    const { project, releaseSchedulerProjectInstance } = acquireSchedulerProjectInstance();

    try {
        const parsedInput = validateAndParseScheduleCalculationInput(input);

        const inputEvent = makesBryntumEventFromParsedScheduleCalculationInput(project, parsedInput, CALC_EVENT_ID);

        // Add temporary resource, event and assignment, and trigger calculation
        project.resourceStore.add({ id: CALC_RESOURCE_ID, name: CALC_RESOURCE_NAME });
        project.eventStore.add(inputEvent);
        project.assignmentStore.add({
            id: CALC_ASSIGNMENT_ID,
            event: CALC_EVENT_ID,
            resource: CALC_RESOURCE_ID
        });

        await project.commitAsync();

        const result = makeScheduleCalculationResultFromBryntumEvent(project, inputEvent, parsedInput);

        return result;
    } finally {
        // Clean up temporary data
        project.eventStore.remove(CALC_EVENT_ID);
        project.assignmentStore.remove(CALC_ASSIGNMENT_ID);
        project.resourceStore.remove(CALC_RESOURCE_ID);

        releaseSchedulerProjectInstance();
    }
}

/**
 * Calculates schedule for multiple inputs with optional sequential dependencies.
 * Leverages Bryntum Scheduler's built-in dependency resolution engine.
 *
 * @param inputs - Array of schedule calculation inputs
 * @param sequential - If true, creates sequential dependencies (default: false)
 * @returns Promise resolving to array of calculated schedules
 */
export async function calculateScheduleList(
    inputs: Array<ScheduleCalculationInput>,
    sequential: boolean = false
): Promise<Array<ScheduleCalculationResult>> {
    if (inputs.length === 0) {
        return [];
    }

    // Single input - delegate to existing calculateSchedule
    if (inputs.length === 1) {
        return [await calculateSchedule(inputs[0])];
    }

    const { project, releaseSchedulerProjectInstance } = acquireSchedulerProjectInstance();

    try {
        const parsedInputs = validateAndParseScheduleCalculationInputList(inputs);

        const inputEvents = parsedInputs.map((input, index) => {
            return makesBryntumEventFromParsedScheduleCalculationInput(
                project,
                input,
                `${CALC_MULTI_EVENT_ID_PREFIX}${index}`
            );
        });

        project.resourceStore.add({ id: CALC_RESOURCE_ID, name: CALC_RESOURCE_NAME });
        project.eventStore.add(inputEvents);
        const assignments = inputEvents.map((event) => ({
            id: `${event.id}${CALC_MULTI_ASSIGNMENT_ID_SUFFIX}`,
            event: event.id,
            resource: CALC_RESOURCE_ID
        }));
        project.assignmentStore.add(assignments);

        if (sequential) {
            const dependencies = inputs.slice(1).map((_, idx) => ({
                id: `${CALC_MULTI_DEP_ID_PREFIX}${idx}`,
                from: `${CALC_MULTI_EVENT_ID_PREFIX}${idx}`,
                to: `${CALC_MULTI_EVENT_ID_PREFIX}${idx + 1}`,
                type: 2 // End-to-Start dependency
            }));
            project.dependencyStore.add(dependencies);

            // Mark dependent events as auto-scheduled (Bryntum calculates their start)
            inputEvents.slice(1).forEach((event) => {
                const eventModel = project.eventStore.getById(event.id) as EventModel;
                if (eventModel) {
                    eventModel.manuallyScheduled = false;
                }
            });
        }

        await project.commitAsync();

        const results = inputEvents.map((event, index) => {
            return makeScheduleCalculationResultFromBryntumEvent(project, event, parsedInputs[index], index);
        });

        return results;
    } finally {
        const tempEventIds = inputs.map((_, idx) => `${CALC_MULTI_EVENT_ID_PREFIX}${idx}`);
        project.eventStore.remove(tempEventIds);

        const tempAssignmentIds = inputs.map(
            (_, idx) => `${CALC_MULTI_EVENT_ID_PREFIX}${idx}${CALC_MULTI_ASSIGNMENT_ID_SUFFIX}`
        );
        project.assignmentStore.remove(tempAssignmentIds);

        if (sequential) {
            const tempDepIds = inputs.slice(1).map((_, idx) => `${CALC_MULTI_DEP_ID_PREFIX}${idx}`);
            project.dependencyStore.remove(tempDepIds);
        }

        project.resourceStore.remove(CALC_RESOURCE_ID);

        releaseSchedulerProjectInstance();
    }
}
